import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang, IMAGES } from "../i18n";
import { useLenis } from "../hooks/useLenis";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Reveal, SectionLabel, MaskLine } from "../components/Motion";
import { Seo } from "../components/Seo";
import {
  ArrowLeft,
  Fingerprint,
  ShieldCheck,
  Smartphone,
  Activity,
  HeartHandshake,
  Repeat,
} from "lucide-react";

const Chip = ({ children }) => (
  <span className="font-jetbrains text-[11px] tracking-[0.18em] uppercase border border-[var(--border)] rounded-full px-4 py-2 text-[var(--text-secondary)]">
    {children}
  </span>
);

const Block = ({ icon: Icon, title, testid, children }) => (
  <Reveal className="border-t border-[var(--border)] pt-12">
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4 flex items-start gap-5">
        <span className="w-11 h-11 rounded-full border border-[var(--gold)]/50 flex items-center justify-center text-[var(--gold)] shrink-0">
          <Icon className="w-5 h-5" />
        </span>
        <h3 className="font-cormorant font-light text-3xl md:text-4xl text-[var(--text-primary)] leading-tight">
          {title}
        </h3>
      </div>
      <div
        data-testid={testid}
        className="lg:col-span-7 lg:col-start-6 space-y-5 font-outfit font-light text-[var(--text-secondary)] leading-relaxed"
      >
        {children}
      </div>
    </div>
  </Reveal>
);

export default function FashionLuxury() {
  useLenis();
  const { t } = useLang();
  const f = t.fashion;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="grain relative">
      <Seo page="fashion" />
      <Nav />
      <main>
        {/* HERO */}
        <section data-testid="fashion-hero" className="relative pt-36 md:pt-44 pb-20 md:pb-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7">
              <Link
                to="/"
                data-testid="fashion-back"
                className="inline-flex items-center gap-2 font-jetbrains text-[11px] tracking-[0.2em] uppercase text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors mb-10"
              >
                <ArrowLeft className="w-4 h-4" /> {f.back}
              </Link>
              <SectionLabel>{f.label}</SectionLabel>
              <h1 className="font-cormorant font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[var(--text-primary)]">
                <MaskLine delay={0.1}>{f.title1}</MaskLine>
                <MaskLine delay={0.2} className="italic text-[var(--gold)]">
                  {f.title2}
                </MaskLine>
              </h1>
              <Reveal delay={0.35}>
                <p className="font-cormorant italic text-2xl md:text-3xl text-[var(--text-secondary)] mt-8 max-w-xl">
                  {f.subtitle}
                </p>
                <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-6 max-w-2xl">
                  {f.intro}
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.25} className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-[2rem] border border-[var(--border)] group">
                <img
                  src={IMAGES.moda1}
                  alt="Etichetta smart Amazonico3000 su capo di lusso"
                  data-testid="fashion-hero-img"
                  className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
                <span className="absolute top-6 left-6 w-6 h-6 border-t border-l border-[var(--gold)]/60" />
                <span className="absolute bottom-6 right-6 w-6 h-6 border-b border-r border-[var(--gold)]/60" />
              </div>
            </Reveal>
          </div>
        </section>

        {/* VIDEO */}
        <section data-testid="fashion-video" className="py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <SectionLabel>{f.videoLabel}</SectionLabel>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)] max-w-xl">
                {f.videoTitle}
              </h2>
              <p className="font-outfit font-light text-[var(--text-secondary)] max-w-md">
                {f.videoSub}
              </p>
            </div>
            <Reveal>
              <div className="relative overflow-hidden rounded-[2rem] border border-[var(--border)]">
                <video
                  data-testid="fashion-video-player"
                  poster={IMAGES.moda1}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/moda.webm" type="video/webm" />
                  <source src={IMAGES.modaVideo} type="video/mp4" />
                </video>
              </div>
            </Reveal>
          </div>
        </section>

        {/* IDENTITY + ANTI-COUNTERFEIT */}
        <section data-testid="fashion-identity" className="py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 space-y-20">
            <Block icon={Fingerprint} title={f.identityTitle} testid="fashion-identity-body">
              <p>{f.identityText}</p>
              <ul className="grid sm:grid-cols-2 gap-x-10">
                {f.identityPoints.map((p, i) => (
                  <li
                    key={i}
                    data-testid={`fashion-identity-point-${i + 1}`}
                    className="flex items-baseline gap-4 border-b border-[var(--border)] py-3"
                  >
                    <span className="font-jetbrains text-[10px] text-[var(--gold)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-outfit text-[var(--text-primary)]">{p}</span>
                  </li>
                ))}
              </ul>
              <p className="font-cormorant italic text-xl md:text-2xl text-[var(--text-primary)]">
                {f.identityOutro}
              </p>
            </Block>

            <Block icon={ShieldCheck} title={f.antiTitle} testid="fashion-anti-body">
              <p>{f.antiP1}</p>
              <p>{f.antiP2}</p>
              <div className="flex flex-wrap gap-3 pt-2">
                {f.antiChips.map((c) => (
                  <Chip key={c}>{c}</Chip>
                ))}
              </div>
              <p className="font-cormorant italic text-xl md:text-2xl text-[var(--text-primary)]">
                {f.antiOutro}
              </p>
            </Block>
          </div>
        </section>

        {/* GALLERY */}
        <section
          data-testid="fashion-gallery"
          className="py-16 md:py-24 bg-[var(--bg-secondary)] border-y border-[var(--border)]"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <SectionLabel>{f.galleryLabel}</SectionLabel>
            <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)] mb-14 max-w-2xl">
              {f.galleryTitle}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[IMAGES.moda1, IMAGES.moda2, IMAGES.moda3].map((src, i) => (
                <Reveal key={i} delay={i * 0.12}>
                  <div className="group relative overflow-hidden rounded-[1.5rem] border border-[var(--border)] aspect-square">
                    <img
                      src={src}
                      alt={`Etichetta smart — dettaglio ${i + 1}`}
                      data-testid={`fashion-gallery-img-${i + 1}`}
                      className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* PASSPORT + SENSORS + RELATION + RESALE */}
        <section className="py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 space-y-20">
            <Block icon={Smartphone} title={f.passportTitle} testid="fashion-passport-body">
              <p>{f.passportP1}</p>
              <p>{f.passportP2}</p>
              <p className="font-cormorant italic text-xl md:text-2xl text-[var(--text-primary)]">
                {f.passportOutro}
              </p>
            </Block>

            <Block icon={Activity} title={f.sensorsTitle} testid="fashion-sensors-body">
              <p>{f.sensorsP1}</p>
              <p>{f.sensorsP2}</p>
              <div className="flex flex-wrap gap-3 pt-2">
                {f.sensorChips.map((c) => (
                  <Chip key={c}>{c}</Chip>
                ))}
              </div>
              <p className="font-cormorant italic text-xl md:text-2xl text-[var(--text-primary)]">
                {f.sensorsOutro}
              </p>
            </Block>

            <Block icon={HeartHandshake} title={f.relationTitle} testid="fashion-relation-body">
              <p>{f.relationP1}</p>
              <p>{f.relationP2}</p>
              <p className="font-cormorant italic text-xl md:text-2xl text-[var(--text-primary)]">
                {f.relationOutro}
              </p>
            </Block>

            <Block icon={Repeat} title={f.resaleTitle} testid="fashion-resale-body">
              <p>{f.resaleP1}</p>
              <p>{f.resaleP2}</p>
            </Block>
          </div>
        </section>

        {/* CLOSING */}
        <section
          data-testid="fashion-closing"
          className="py-24 md:py-36 bg-[var(--bg-secondary)] border-t border-[var(--border)]"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
            <Reveal>
              <h2 className="font-cormorant font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[var(--text-primary)]">
                {f.closingTitle}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-8 max-w-3xl mx-auto">
                {f.closingP1}
              </p>
              <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-4 max-w-3xl mx-auto">
                {f.closingP2}
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="font-cormorant italic text-2xl md:text-3xl text-[var(--gold)] mt-14 max-w-3xl mx-auto leading-snug">
                “{f.closingQuote}”
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <Link
                to="/"
                data-testid="fashion-back-cta"
                className="inline-flex items-center gap-3 mt-14 rounded-full border border-[var(--gold)] px-8 py-4 font-outfit text-xs tracking-[0.2em] uppercase text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors duration-300"
              >
                <ArrowLeft className="w-4 h-4" /> {f.back}
              </Link>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
